import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg, uploadMedia } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Navigation, Camera, X, Lock } from "lucide-react";

export function NewRequest() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    raw_text: "",
    category: params.get("categoria") || "",
    urgent: true,
    address: "",
    budget_min: "",
    budget_max: "",
  });
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const cats = useCategories();

  const locate = () => {
    if (!navigator.geolocation) { toast.error("Geolocalizzazione non supportata dal browser"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); toast.success("Posizione acquisita"); },
      () => { setLocating(false); toast.error("Impossibile ottenere la posizione"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const addPhoto = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    setPhotos((p) => [...p, ...files]);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const media_ids = [];
      for (const f of photos) {
        const res = await uploadMedia(f);
        const id = res.id || res.media_id;
        if (id) media_ids.push(id);
      }
      const payload = {
        raw_text: form.raw_text,
        address: form.address,
        urgent: form.urgent,
        media_ids,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        category_override: form.category && form.category !== "altro" ? form.category : null,
        budget_type: form.budget_min || form.budget_max ? "range" : "unknown",
        budget_min: form.budget_min ? Number(form.budget_min) : null,
        budget_max: form.budget_max ? Number(form.budget_max) : null,
      };
      const { data } = await api.post("/requests", payload);
      toast.success("Richiesta pubblicata! I professionisti la vedranno subito.");
      navigate(`/richieste/${data.id || data.request_id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 pt-6 sm:pt-10 space-y-6 sm:max-w-xl sm:mx-auto" data-testid="new-request-page">
      <div className="space-y-1">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Nuova richiesta</h1>
        <p className="text-sm text-slate-600">Descrivi il problema: la vedranno subito i professionisti disponibili vicino a te.</p>
      </div>

      <form onSubmit={submit} className="space-y-5" data-testid="new-request-form">
        <div className="space-y-2">
          <Label>Categoria <span className="text-slate-400 font-normal">(opzionale)</span></Label>
          <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
            <SelectTrigger data-testid="request-category-select" className="h-14 bg-slate-50 border-transparent rounded-2xl">
              <SelectValue placeholder="Scegli la categoria" />
            </SelectTrigger>
            <SelectContent>
              {cats.map((c) => <SelectItem key={c.key || c.id} value={c.key || c.id} data-testid={`category-option-${c.key || c.id}`}>{c.label}</SelectItem>)}
              <SelectItem value="altro" data-testid="category-option-altro">Altro / Non in lista</SelectItem>
            </SelectContent>
          </Select>
          {form.category === "altro" && (
            <p className="text-xs text-slate-500 leading-relaxed" data-testid="altro-hint">
              Nessun problema: descrivi bene il problema qui sotto e la richiesta verrà smistata automaticamente ai professionisti giusti.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="desc">Descrivi il problema</Label>
          <Textarea id="desc" data-testid="request-description-input" required minLength={3} maxLength={1000} rows={4} value={form.raw_text}
            onChange={(e) => setForm((f) => ({ ...f, raw_text: e.target.value }))}
            className="bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl resize-none"
            placeholder="Es. Perdita d'acqua sotto il lavandino della cucina, serve un intervento urgente..." />
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 flex items-center justify-between">
          <div>
            <p className="font-semibold text-slate-900 text-sm">Intervento urgente</p>
            <p className="text-xs text-slate-500">Priorità ai professionisti "Disponibile ora"</p>
          </div>
          <Switch data-testid="request-urgent-switch" checked={form.urgent} onCheckedChange={(v) => setForm((f) => ({ ...f, urgent: v }))} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address">Zona / città</Label>
          <div className="flex gap-2">
            <Input id="address" data-testid="request-address-input" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl flex-1" placeholder="Es. Roma, zona Centro" />
            <Button type="button" data-testid="geolocate-btn" variant="outline" onClick={locate} disabled={locating}
              className={`h-14 w-14 rounded-2xl shrink-0 ${coords ? "border-emerald-300 text-emerald-600" : ""}`}>
              {locating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Navigation className="w-5 h-5" />}
            </Button>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed flex gap-1.5" data-testid="address-privacy-note">
            <Lock className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span>Pubblica solo la zona: l'indirizzo esatto resta privato e lo condividerai tu in chat, solo con il professionista che avrai scelto. La posizione GPS serve solo a mostrare la richiesta ai professionisti vicini.</span>
          </p>
          {coords && <p className="text-xs text-emerald-600 font-semibold" data-testid="coords-ok">Posizione GPS acquisita</p>}
        </div>

        <div className="space-y-2">
          <Label>Budget indicativo (opzionale)</Label>
          <div className="flex gap-2">
            <Input data-testid="budget-min-input" type="number" min="0" value={form.budget_min} onChange={(e) => setForm((f) => ({ ...f, budget_min: e.target.value }))}
              className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="Min €" />
            <Input data-testid="budget-max-input" type="number" min="0" value={form.budget_max} onChange={(e) => setForm((f) => ({ ...f, budget_max: e.target.value }))}
              className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="Max €" />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Foto (max 3)</Label>
          <div className="flex gap-2 flex-wrap">
            {photos.map((p, i) => (
              <div key={i} className="relative w-20 h-20 rounded-2xl overflow-hidden border border-slate-200">
                <img src={URL.createObjectURL(p)} alt="" className="w-full h-full object-cover" />
                <button type="button" data-testid={`remove-photo-${i}`} onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
              </div>
            ))}
            {photos.length < 3 && (
              <label data-testid="add-photo-btn" className="w-20 h-20 rounded-2xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-whoo hover:text-whoo transition-colors">
                <Camera className="w-5 h-5" />
                <input type="file" accept="image/*" multiple className="hidden" onChange={addPhoto} />
              </label>
            )}
          </div>
        </div>

        <Button data-testid="submit-request-btn" type="submit" disabled={loading}
          className="w-full h-14 rounded-full bg-whoo hover:bg-whoo-dark text-white font-heading font-semibold text-base active:scale-[0.98] transition-transform shadow-lg shadow-orange-500/25">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Pubblica richiesta"}
        </Button>
      </form>
    </div>
  );
}
